import React, { useContext } from 'react';
import { CSSProperties } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import useBrand from 'Hooks/useBrand';
import { Body2, Body3 } from 'components/shareable/Typography';
import ModalSymphony from 'elements/ModalSymphony';
import ButtonSymphony from 'elements/ButtonSymphony';
import { PlanType } from 'modules/Const';
import { UpgradeModalContext } from 'Hooks/UpgradeModalContext';
import useMessageLimits from 'Hooks/useMessageLimits';

interface MessageLimitEnforcementModalProps {

    className?: string;
}

const MessageLimitEnforcementModal: React.FC<MessageLimitEnforcementModalProps> = ({
    className = ''
}) => {
    const {
        isEnforcementModalOpen: isOpen,
        closeEnforcementModal: onClose,
    } = useMessageLimits()
    const { isAuthenticated, loginWithRedirect, loginWithPopup } = useAuth0();
    const { currentBrand } = useBrand();
    const { handleOpenModal } = useContext(UpgradeModalContext);

    // Helper function to get the appropriate message based on user status
    const getModalContent = () => {
        if (!isAuthenticated) {
            return {
                title: 'You\'ve run out of free messages for the month.',
                message: 'Please sign up or login and subscribe to a paid plan to unlock more messages.',
                primaryButtonText: 'Sign Up',
                primaryButtonAction: () => loginWithPopup()
            };
        }

        // User is authenticated
        const userTier = currentBrand?.tier;

        if (userTier === PlanType.LITE || userTier === PlanType.STARTER) {
            return {
                title: 'You\'ve run out of free messages',
                message: 'Sign up for a profile to reset your limit for the month. You can also subscribe to the "Growth" tier to unlock 10x more messages.',
                primaryButtonText: 'Upgrade to Growth',
                primaryButtonAction: () => {
                    onClose();
                    handleOpenModal({
                        source: 'message_limit_enforcement',
                        highlightedPlan: PlanType.GROWTH,
                        onlyShowPlans: [PlanType.GROWTH],
                        upgradeCallToAction: 'Upgrade to Growth for 10x more messages'
                    });
                },
                secondaryButtonText: 'Close',
                secondaryButtonAction: onClose
            };
        }

        if (userTier === PlanType.PRO) {
            return {
                title: 'You\'ve run out of messages',
                message: 'Upgrade to "Growth" to unlock 10x more messages.',
                primaryButtonText: 'Upgrade to Growth',
                primaryButtonAction: () => {
                    onClose();
                    handleOpenModal({
                        source: 'message_limit_enforcement',
                        highlightedPlan: PlanType.GROWTH,
                        onlyShowPlans: [PlanType.GROWTH],
                        upgradeCallToAction: 'Upgrade to Growth for 10x more messages'
                    });
                },
                secondaryButtonText: 'Close',
                secondaryButtonAction: onClose
            };
        }

        if (userTier === PlanType.GROWTH) {
            return {
                title: 'You\'ve reached your message limit',
                message: 'Please contact your team at megh@symphony.to to get higher message limits.',
                primaryButtonText: 'Contact Support',
                primaryButtonAction: () => {
                    window.location.href = 'mailto:megh@symphony.to?subject=Higher Message Limits Request';
                },
                secondaryButtonText: 'Close',
                secondaryButtonAction: onClose
            };
        }

        // Default case (shouldn't happen but good to have)
        return {
            title: 'You\'ve run out of messages',
            message: 'Please upgrade your plan to get more messages.',
            primaryButtonText: 'Upgrade',
            primaryButtonAction: () => {
                onClose();
                handleOpenModal({
                    source: 'message_limit_enforcement',
                    upgradeCallToAction: 'Upgrade your plan for more messages'
                });
            },
            secondaryButtonText: 'Close',
            secondaryButtonAction: onClose
        };
    };

    const modalContent = getModalContent();

    return (
        <ModalSymphony
            className={`messageLimitEnforcementModal ${className}`}
            isOpen={isOpen}
            onClose={onClose}
            title={modalContent.title}
        >
            <div className="messageLimitEnforcementContent" style={sty.modalContent}>
                <Body2 style={sty.messageText}>
                    {modalContent.message}
                </Body2>

                <div className="messageLimitEnforcementButtons" style={sty.buttonsContainer}>
                    <ButtonSymphony
                        className="messageLimitEnforcementPrimaryButton"
                        variant="primary"
                        onClick={modalContent.primaryButtonAction}
                        style={sty.primaryButton}
                    >
                        {modalContent.primaryButtonText}
                    </ButtonSymphony>
                </div>
            </div>
        </ModalSymphony>
    );
};

const sty: Record<string, CSSProperties> = {
    modalContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
        width: '100%',
        padding: '16px 0',
    },
    messageText: {
        lineHeight: '1.5',
        textAlign: 'center',
        color: '#333',
    },
    buttonsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        width: '100%',
    },
    primaryButton: {
        width: '100%',
    },
    secondaryButton: {
        width: '100%',
    },
};

export default MessageLimitEnforcementModal; 